import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import ChekinPro from 'ChekinPro';
import ChekinProSettings from 'ChekinProSettings';
import {PATHS} from 'Routes';
import {
  FormTypes,
  getNewToken,
  OPTION_VALUES,
  SEARCH_RESERVATION_FORM_NAMES,
} from 'pages/SearchReservation/utils';
import {RESERVATION_ID_KEY, SHARE_LINK, STATUSES} from 'utils/constants';
import {useErrorModal, useModalControls, usePath, useTimeoutRef} from 'hooks';
import {LocalStorage, useStatus} from '@guestapp/core';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservationNotFound} from '../../useReservationNotFound';
import {StatusModalStates} from 'components/StatusModal';
import PageUniversalLink from 'components/PageUniversalLink';
import {ViewMainInfo} from '../../components/ViewMainInfo';
import {InputController as Input} from '@guestapp/ui';
import {ReservationNotFoundModal} from 'components/ReservationNotFoundModal';
import {SearchingBookingModal} from '../../components/SearchingBookingModal';
import {createSession} from 'components/sessions/utils';
import {
  ContentButton,
  CustomBackButton,
  StyledBackIcon,
  StyledButton,
  StyledFormFieldWrapper,
  Wrapper,
} from './styled';

function BookingCodeView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {isLoading, setStatus} = useStatus();
  const {findReservation} = useChekinSDK();
  const {state} = useLocation();
  const {ErrorModal, displayError} = useErrorModal();
  const timeoutRef = useTimeoutRef();
  const [modalRegisterGuestStatus, setModalRegisterGuestStatus] = React.useState(
    StatusModalStates.IDLE,
  );

  const {
    isOpen: isOpenReservationStatusModal,
    openModal: openReservationStatusModal,
    closeModal: closeReservationStatusModal,
  } = useModalControls();

  const {canCreate, isNotFoundModalOpen, openNotFoundModal, closeNotFoundModal} =
    useReservationNotFound();

  const {
    register,
    control,
    handleSubmit,
    trigger,
    formState: {errors, isSubmitted},
    watch,
  } = useForm<FormTypes>();
  const bookingReference = watch(SEARCH_RESERVATION_FORM_NAMES.booking_reference);

  const {mutate} = useMutation(
    (formData: FormTypes) => {
      const booking_reference = formData[SEARCH_RESERVATION_FORM_NAMES.booking_reference];

      const params = {
        booking_reference: booking_reference,
        housing_id: state?.housingId,
      };

      return findReservation(params);
    },
    {
      onSuccess: data => {
        if (data) {
          const reservationId = data?.results?.[0]?.id;
          const reservationSigUpFormLink = data?.results?.[0]?.signup_form_link;
          if (reservationId) {
            if (ChekinProSettings.isSDK) {
              setStatus(STATUSES.success);
              setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
              ChekinPro.events.onReservationFoundFromHousing({id: reservationId});
              return;
            }

            createSession({reservationId});

            LocalStorage.set(RESERVATION_ID_KEY, reservationId);
            LocalStorage.set(SHARE_LINK, reservationSigUpFormLink);
            const tokenFromUrl = getNewToken(reservationSigUpFormLink);
            window.location.href = `${
              import.meta.env.VITE_APP_PUBLIC_URL
            }/${tokenFromUrl}`;
            setModalRegisterGuestStatus(StatusModalStates.SUCCESS);
          } else {
            setModalRegisterGuestStatus(StatusModalStates.IDLE);
            closeReservationStatusModal();
            setStatus(STATUSES.idle);

            canCreate ? goToNewReservation() : openNotFoundModal();
          }
        }
      },
      onError: error => {
        setStatus(STATUSES.error);
        closeReservationStatusModal();
        displayError(error);
      },
    },
  );

  const onSubmit = (formData: FormTypes) => {
    setStatus(STATUSES.loading);
    setModalRegisterGuestStatus(StatusModalStates.LOADING);
    openReservationStatusModal();
    mutate(formData);
  };

  const goToNewReservation = () => {
    navigate(getFullPath(PATHS.searchReservation.newReservation), {
      replace: true,
      state: {
        housingId: state?.housingId,
        country: state?.housingCountry,
        from: OPTION_VALUES.booking_reference,
        mode: state?.mode,
      },
    });
  };

  React.useEffect(
    function changeTranslationErrorMessages() {
      if (isSubmitted) {
        trigger();
      }
    },
    [isSubmitted, trigger],
  );

  React.useEffect(() => {
    if (
      !ChekinProSettings.isSDK &&
      modalRegisterGuestStatus === StatusModalStates.SUCCESS
    ) {
      timeoutRef.current = setTimeout(() => {
        closeReservationStatusModal();
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [closeReservationStatusModal, modalRegisterGuestStatus, timeoutRef]);

  return (
    <PageUniversalLink className="booking-code-view">
      <Wrapper>
        <ViewMainInfo
          title={t('find_your_booking')}
          subTitle={t('enter_the_reference_code_assigned_to_your_booking')}
        />

        {!state?.mode && (
          <CustomBackButton
            label={t('back')}
            customIcon={<StyledBackIcon name="backArrow" size={15} />}
            iconProps={{position: 'left'}}
            onClick={() =>
              navigate(getFullPath(PATHS.searchReservation.main), {
                state: {mode: OPTION_VALUES.booking_reference},
              })
            }
            width="auto"
            className="summary-view__back-btn"
            link
          />
        )}

        <StyledFormFieldWrapper>
          <Input
            {...register(SEARCH_RESERVATION_FORM_NAMES.booking_reference, {
              required: t('required'),
            })}
            label={t('booking_reference_code')}
            control={control}
            error={errors[SEARCH_RESERVATION_FORM_NAMES.booking_reference]?.message}
            autoCorrect="off"
            spellCheck={false}
          />
        </StyledFormFieldWrapper>

        <ContentButton>
          <StyledButton
            label={t('confirm')}
            disabled={isLoading || !bookingReference}
            onClick={handleSubmit(onSubmit)}
          />
        </ContentButton>
      </Wrapper>
      <SearchingBookingModal
        isOpen={isOpenReservationStatusModal}
        status={modalRegisterGuestStatus}
      />
      <ErrorModal />

      <ReservationNotFoundModal open={isNotFoundModalOpen} onClose={closeNotFoundModal} />
    </PageUniversalLink>
  );
}

export {BookingCodeView};
